import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { useNavigate, useOpenConversation } from "../../../common";
import { SmallCustomIcon, TimelineMarker } from "../../layer00";
import { StyledCard, BoZoneDivider, EmptyStateCard } from "../../layer01";
import { BoZoneHistoryChart, BoZoneLearnedItem } from "../../layer06";
import * as ds from "./dataService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { useState } from "react";
import messagesIllustration from "resources/images/messages-illustration.png";
import { CompletedCheckInSummary } from "app/containers/TeamsTab/types";

type BoZoneTrendsCardProps = {
  userAccountId: number | null;
  checkInSummary?: CompletedCheckInSummary | null;
  isCheckInSummaryLoading?: boolean;
};

const PAGE_SIZE = 5;

const LearnedItemSkeleton = ({
  isFirst,
  isLast,
}: {
  isFirst: boolean;
  isLast: boolean;
}) => (
  <Stack
    direction="row"
    position="relative"
    marginLeft={{
      xs: "2rem",
      sm: "3rem",
    }}
    paddingY=".5rem"
    paddingLeft={{
      xs: ".75rem",
      sm: "1rem",
    }}
    paddingRight={{
      xs: ".5rem",
      sm: "1.5rem",
    }}
  >
    <TimelineMarker overhang=".75rem" isFirst={isFirst} isLast={isLast} />
    <Stack direction="column" rowGap=".5rem" flex="1 1 0" minWidth={0}>
      <Stack direction="row" columnGap=".5rem" alignItems="center">
        <Skeleton width="3.5rem" height={20} />
        <Skeleton
          variant="rounded"
          width="4.5rem"
          height="1.25rem"
          sx={{ borderRadius: "1rem" }}
        />
      </Stack>
      <Stack direction="column" rowGap=".125rem">
        <Skeleton width="90%" height={14} />
        <Skeleton width="65%" height={14} />
      </Stack>
    </Stack>
  </Stack>
);

export const BoZoneTrendsCard = ({
  userAccountId,
  checkInSummary,
  isCheckInSummaryLoading,
}: BoZoneTrendsCardProps) => {
  const navigate = useNavigate();
  const openConversation = useOpenConversation();
  const { data, isLoading } = ds.useLearnedItemsQuery(userAccountId);
  const { data: history } = ds.useHistoryQuery(checkInSummary);
  const [totalItemsShowing, setTotalItemsShowing] = useState(PAGE_SIZE);
  const visibleItems = data.slice(0, totalItemsShowing);
  const isConservative = checkInSummary?.visibilityMode === "conservative";

  return (
    <StyledCard
      title={isConservative ? "Activity History" : "Trends + History"}
    >
      <Stack direction="column" rowGap=".8125rem">
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          columnGap=".75rem"
          rowGap=".75rem"
          alignItems="center"
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate("/user/boBrain")}
            startIcon={<SmallCustomIcon icon="SLIDER" stroke="#425CC7" />}
            className="border-0"
          >
            View/Edit Bo&rsquo;s Brain
          </Button>
        </Stack>
        {!isConservative && (
          <BoZoneHistoryChart
            title="Your historical scores"
            labels={history.labels}
            pointLabels={history.pointLabels}
            series={history.series}
            isLoading={isCheckInSummaryLoading}
          />
        )}
        <BoZoneDivider />
        <Typography variant="formLabel" color="#334C71">
          What Bo has learned
        </Typography>
        <Stack direction="column" rowGap=".75rem">
          {isLoading &&
            [0, 1, 2].map((i) => (
              <LearnedItemSkeleton key={i} isFirst={i === 0} isLast={i === 2} />
            ))}
          {!isLoading && data.length === 0 && (
            <EmptyStateCard
              image={messagesIllustration}
              title="Bo is still getting to know you"
              body="Complete a check-in to see what Bo learns."
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate("/user/checkIn")}
                >
                  Start a check-in
                </Button>
              }
            />
          )}
          {!isLoading &&
            visibleItems.map((node, index) => (
              <BoZoneLearnedItem
                key={node.id}
                date={node.date}
                category={node.category}
                categoryVariant={node.categoryVariant}
                summary={node.summary}
                isFirst={index === 0}
                isLast={index === visibleItems.length - 1}
                onEdit={
                  node.conversation
                    ? () => openConversation(node.conversation!)
                    : undefined
                }
              />
            ))}
          {!isLoading && totalItemsShowing < data.length ? (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setTotalItemsShowing((s) => s + PAGE_SIZE);
              }}
              className="border-0"
            >
              View More
              <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </StyledCard>
  );
};
